import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Chip,
} from '@mui/material';
import { Visibility as ViewIcon } from '@mui/icons-material';

interface VersionDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  version: any;
  viewType: 'global' | 'fund';
}

const DetailItem: React.FC<{ label: string; value: any }> = ({ label, value }) => (
  <Grid item xs={12} sm={6}>
    <Typography variant="subtitle2" color="text.secondary">{label}</Typography>
    {Array.isArray(value) ? (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {value.map((item, index) => (
          <Chip key={index} label={item} size="small" />
        ))}
      </Box>
    ) : (
      <Typography variant="body2">{value?.toString() || 'N/A'}</Typography>
    )}
  </Grid>
);

const VersionDetailsDialog: React.FC<VersionDetailsDialogProps> = ({ open, onClose, version, viewType }) => {
  if (!version) return null;

  const { thesis_data, change_summary, changed_fields, creator, created_at, version_number } = version;

  const isGlobalView = viewType === 'global';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ViewIcon color="primary" />
          Version {version_number} Details
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 2 }}>
            <Typography variant="h6">Change Information</Typography>
            <Grid container spacing={2}>
                <DetailItem label="Change Summary" value={change_summary} />
                <DetailItem label="Changed Fields" value={changed_fields} />
                <DetailItem label="Created By" value={creator.email || 'Unknown'} />
                <DetailItem label="Created At" value={new Date(created_at).toLocaleString()} />
            </Grid>
        </Box>
        
        <Typography variant="h6">Thesis Data</Typography>
        <Grid container spacing={2}>
          {thesis_data.name && <DetailItem label="Name" value={thesis_data.name} />}
          {thesis_data.description && <DetailItem label="Description" value={thesis_data.description} />}
          {thesis_data.risk_appetite && <DetailItem label="Risk Appetite" value={thesis_data.risk_appetite} />}
          {thesis_data.core_investment_themes && <DetailItem label="Core Investment Themes" value={thesis_data.core_investment_themes} />}
          {thesis_data.value_creation_levers && <DetailItem label="Value Creation Levers" value={thesis_data.value_creation_levers} />}
          {thesis_data.deal_breakers && <DetailItem label="Deal Breakers" value={thesis_data.deal_breakers} />}
          {thesis_data.preferred_business_models && <DetailItem label="Preferred Business Models" value={thesis_data.preferred_business_models} />}
          {thesis_data.sector_focus && <DetailItem label="Sector Focus" value={thesis_data.sector_focus} />}
          {thesis_data.industry_focus && <DetailItem label="Industry Focus" value={thesis_data.industry_focus} />}
          {isGlobalView && thesis_data.firm_profile?.geographic_focus && <DetailItem label="Geographic Focus" value={thesis_data.firm_profile.geographic_focus} />}
          {isGlobalView && thesis_data.macro_trends && <DetailItem label="Macro Trends" value={thesis_data.macro_trends} />}
          {isGlobalView && thesis_data.micro_trends && <DetailItem label="Micro Trends" value={thesis_data.micro_trends} />}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default VersionDetailsDialog;